import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, CheckCircle2, ChevronDown, Clock, Eye, FileText, Image as ImageIcon, Pencil, Plus, RefreshCw, Trash2, X, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { decodeTokenService } from "../../../services/decodeTokenService";
import { handleApi } from "../../../utils/handleApi";
import Loader from "../../../components/common/Loader";
import { BaseModal } from "../../../components/modals/BaseModal";
import {
  createTemplate,
  deleteTemplate,
  getWhatsAppAccount,
  listTemplates,
  setDefaultTemplates,
  syncTemplates,
  updateTemplate,
  type DefaultTemplatesPayload,
  type TemplateCategory,
  type TemplateStatus,
  type WhatsAppAccount,
  type WhatsAppTemplate,
} from "../../../services/whatsapp/whatsappService";

type DefaultSlot =
  | "confirmation_template_name"
  | "reminder_template_name"
  | "fallback_template_name"
  | "cart_recovery_template_name"
  | "order_confirmed_template_name";

const DEFAULT_SLOTS: DefaultSlot[] = [
  "confirmation_template_name",
  "reminder_template_name",
  "fallback_template_name",
  "cart_recovery_template_name",
  "order_confirmed_template_name",
];

const LANGUAGE_OPTIONS = ["es", "en", "pt_BR"] as const;
const CATEGORY_OPTIONS: TemplateCategory[] = ["UTILITY", "MARKETING"];

const STATUS_STYLE: Record<TemplateStatus, { bg: string; text: string; Icon: typeof CheckCircle2 }> = {
  APPROVED: { bg: "bg-green-50 border-green-200", text: "text-green-700", Icon: CheckCircle2 },
  PENDING: { bg: "bg-yellow-50 border-yellow-200", text: "text-yellow-700", Icon: Clock },
  IN_APPEAL: { bg: "bg-yellow-50 border-yellow-200", text: "text-yellow-700", Icon: Clock },
  REJECTED: { bg: "bg-red-50 border-red-200", text: "text-red-700", Icon: AlertCircle },
  PAUSED: { bg: "bg-gray-100 border-gray-200", text: "text-gray-600", Icon: AlertCircle },
  DISABLED: { bg: "bg-gray-100 border-gray-200", text: "text-gray-600", Icon: AlertCircle },
};

interface MetaErrorInfo {
  message: string;
}

type TemplateButton = {
  type?: string;
  text?: string | null;
};

type TemplateComponent = {
  type?: string;
  text?: string;
  format?: string;
  buttons?: TemplateButton[];
  example?: unknown;
};

/**
 * Unwrap the rich error detail our backend propagates for Meta template
 * failures (backend #52 returns `{message, meta_error}` — older deploys
 * return a plain string). We extract the friendliest message for toasts.
 */
const extractMetaError = (error: unknown): MetaErrorInfo => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string") {
    return { message: detail };
  }
  if (detail && typeof detail === "object") {
    const detailObj = detail as {
      message?: string;
      meta_error?: {
        error_user_msg?: string;
        message?: string;
      };
    };
    const metaErr = detailObj.meta_error || {};
    const message: string =
      metaErr.error_user_msg ||
      metaErr.message ||
      detailObj.message ||
      "Meta rejected the request";
    return { message };
  }
  return { message: "Error desconocido" };
};

const getTemplateBody = (template: WhatsAppTemplate): string => {
  if (template.body_text) return template.body_text;
  const body = (template.components || []).find(
    (component) => String(component?.type || "").toUpperCase() === "BODY"
  );
  return body?.text || "";
};

const getTemplateButtons = (template: WhatsAppTemplate): string[] => {
  const buttonComponent = (template.components || []).find(
    (component) => String(component?.type || "").toUpperCase() === "BUTTONS"
  ) as TemplateComponent | undefined;
  return (buttonComponent?.buttons || [])
    .map((button) => button?.text)
    .filter((text): text is string => Boolean(text));
};

const getTemplateHeader = (template: WhatsAppTemplate): TemplateComponent | undefined => {
  return (template.components || []).find(
    (component) => String(component?.type || "").toUpperCase() === "HEADER"
  ) as TemplateComponent | undefined;
};

const getTemplateHeaderFormat = (template: WhatsAppTemplate): string => {
  return String(getTemplateHeader(template)?.format || "").toUpperCase();
};

const extractExampleImageUrl = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedUrl = extractExampleImageUrl(item);
      if (nestedUrl) return nestedUrl;
    }
    return null;
  }
  if (value && typeof value === "object") {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const nestedUrl = extractExampleImageUrl(item);
      if (nestedUrl) return nestedUrl;
    }
  }
  return null;
};

const getTemplateHeaderImageUrl = (template: WhatsAppTemplate): string | null => {
  const header = getTemplateHeader(template);
  if (!header || String(header.format || "").toUpperCase() !== "IMAGE") return null;
  return extractExampleImageUrl(header.example);
};

const TEMPLATE_PREVIEW_SAMPLES = [
  "Juan",
  "4196",
  "Producto X",
  "1",
  "150000",
  "Calle 123",
  "Bogota",
  "Cundinamarca",
  "Colombia",
  "Ejemplo",
];

const getPreviewVariableCount = (body: string, templateCount: number) => {
  let max = templateCount || 0;
  for (const match of body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    max = Math.max(max, Number(match[1]));
  }
  return max;
};

const getPreviewVariables = (body: string, templateCount: number) => {
  const count = getPreviewVariableCount(body, templateCount);
  return Array.from({ length: count }, (_, index) => ({
    token: `{{${index + 1}}}`,
    value: TEMPLATE_PREVIEW_SAMPLES[index] ?? `Ejemplo ${index + 1}`,
  }));
};

const renderTemplatePreviewBody = (body: string, templateCount: number) => {
  const variables = getPreviewVariables(body, templateCount);
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, rawIndex) => {
    const index = Number(rawIndex) - 1;
    return variables[index]?.value ?? match;
  });
};

const buildTemplateComponents = (
  body: string,
  withButtons: boolean,
  headerFormat?: "IMAGE"
): TemplateComponent[] => {
  const components: TemplateComponent[] = [];
  if (headerFormat === "IMAGE") {
    components.push({ type: "HEADER", format: "IMAGE" });
  }
  components.push({ type: "BODY", text: body });
  if (withButtons) {
    components.push({
      type: "BUTTONS",
      buttons: [
        { type: "QUICK_REPLY", text: "Confirmar Pedido" },
        { type: "QUICK_REPLY", text: "Modificar Datos" },
      ],
    });
  }
  return components;
};

const TemplatesPanel = () => {
  const { language } = useAuth();
  const t = (key: string, fallback: string) => dictionary[key]?.[language] ?? fallback;

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [account, setAccount] = useState<WhatsAppAccount | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState<WhatsAppTemplate | null>(null);
  const [viewTarget, setViewTarget] = useState<WhatsAppTemplate | null>(null);
  const [showRecommendedModal, setShowRecommendedModal] = useState(false);
  const [savingDefault, setSavingDefault] = useState<DefaultSlot | null>(null);
  const [clearedDefaultSlots, setClearedDefaultSlots] = useState<Partial<Record<DefaultSlot, boolean>>>({});
  const [creatingRecommended, setCreatingRecommended] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppTemplate | null>(null);
  const [deletingInFlight, setDeletingInFlight] = useState(false);

  const ownerId = useMemo(() => decodeTokenService.getUserIdFromToken(), []);

  const load = async () => {
    if (!ownerId) return;
    setLoading(true);
    const [accountResult, listResult] = await Promise.all([
      handleApi(getWhatsAppAccount(ownerId), false),
      handleApi(listTemplates(ownerId), false),
    ]);
    if (accountResult.res) setAccount(accountResult.res);
    if (listResult.res) setTemplates(listResult.res);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    if (!ownerId) return;
    setSyncing(true);
    const { res, error } = await handleApi(syncTemplates(ownerId), false);
    if (res) {
      toast.success(t("ws-templates-synced", `Sincronizado: ${res.synced}`).replace("{n}", String(res.synced)));
      const list = await handleApi(listTemplates(ownerId), false);
      if (list.res) setTemplates(list.res);
    } else if (error) {
      toast.error(t("ws-templates-sync-error", "No se pudo sincronizar con Meta"));
    }
    setSyncing(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!ownerId || !deleteTarget) return;
    const name = deleteTarget.name;
    setDeletingInFlight(true);
    const { res, error } = await handleApi(deleteTemplate(ownerId, name), false);
    if (res) {
      toast.success(t("ws-templates-deleted", "Template eliminado"));
      setTemplates((prev) => prev.filter((x) => x.name !== name));
      setDeleteTarget(null);
    } else if (error) {
      const info = extractMetaError(error);
      toast.error(info.message.slice(0, 250) || t("ws-templates-delete-error", "No se pudo eliminar"));
    }
    setDeletingInFlight(false);
  };

  const handleSetDefault = async (slot: DefaultSlot, value: string) => {
    if (!ownerId) return;
    setSavingDefault(slot);
    const payload: DefaultTemplatesPayload = { [slot]: value || null };
    const { res, error } = await handleApi(setDefaultTemplates(ownerId, payload), false);
    if (res) {
      toast.success(t("ws-templates-default-saved", "Default guardado"));
      setAccount((prev) => (prev ? { ...prev, ...res } : prev));
      setClearedDefaultSlots((prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });
    } else if (error) {
      const info = extractMetaError(error);
      toast.error(info.message.slice(0, 250) || t("ws-templates-default-error", "No se pudo guardar el default"));
    }
    setSavingDefault(null);
  };

  const approvedTemplates = useMemo(() => templates.filter((x) => x.status === "APPROVED"), [templates]);
  const defaultClearedNotice = t(
    "ws-templates-default-cleared-after-update",
    "Esta plantilla quedó vacía porque la actualizaste. Espera a que Meta la apruebe y vuelve a seleccionarla."
  );
  const getDefaultNotice = (slot: DefaultSlot) => {
    if (clearedDefaultSlots[slot]) return defaultClearedNotice;
    return undefined;
  };

  // A preset "already exists" when a template with the exact canonical
  // _fluxi name is already present locally (any status, not only APPROVED
  // — if it's PENDING/REJECTED we also don't want to re-create it). Match
  // by name (not by shape) because the canonical Fluxi templates have
  // specific names we rely on for the order flow defaults.
  const recommendedMissing = useMemo(
    () => (Object.keys(PRESETS) as Array<Exclude<Preset, "custom">>).filter((k) => {
      const spec = PRESETS[k];
      const exists = templates.some(
        (t) => t.name === spec.suggestedName && t.language === "es"
      );
      return !exists;
    }),
    [templates]
  );

  const createRecommendedTemplates = async (
    selectedRecommended: Array<Exclude<Preset, "custom">>
  ) => {
    if (!ownerId || selectedRecommended.length === 0) return;
    setCreatingRecommended(true);

    let created = 0;
    const errors: string[] = [];
    for (const key of selectedRecommended) {
      const spec = PRESETS[key];
      const components = buildTemplateComponents(
        spec.recommendedBody,
        spec.buttons,
        spec.headerFormat
      );
      const { res, error } = await handleApi(
        createTemplate({
          owner_id: ownerId,
          name: spec.suggestedName,
          language: "es",
          category: spec.category,
          components,
        }),
        false
      );
      if (res) {
        created += 1;
        setTemplates((prev) => [res, ...prev]);
      } else if (error) {
        const info = extractMetaError(error);
        errors.push(`${spec.suggestedName}: ${info.message}`);
      }
    }
    if (created > 0) {
      toast.success(
        t(
          "ws-templates-recommended-created",
          `Enviadas ${created} plantilla(s) a Meta (PENDING)`
        ).replace("{n}", String(created))
      );
    }
    for (const err of errors) {
      toast.error(err.slice(0, 250));
    }
    setCreatingRecommended(false);
  };

  const openCreateModal = () => {
    setEditTarget(null);
    setShowCreateModal(true);
  };

  const openEditModal = (template: WhatsAppTemplate) => {
    setEditTarget(template);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setEditTarget(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <AlertCircle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
        <p className="text-sm text-gray-600">
          {t("ws-templates-no-account", "Primero conecta tu cuenta de WhatsApp Business para gestionar templates.")}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8 font-['Plus_Jakarta_Sans']">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {t("ws-templates-title", "Plantillas de WhatsApp")}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t("ws-templates-subtitle", "Crea y gestiona las plantillas aprobadas por Meta para enviar mensajes iniciales.")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing
              ? t("ws-templates-syncing", "Sincronizando...")
              : t("ws-templates-sync", "Sincronizar con Meta")}
          </button>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t("ws-templates-create", "Nueva plantilla")}
          </button>
        </div>
      </div>

      {/* Recommended templates banner — only when some canonical ones are missing */}
      {recommendedMissing.length > 0 && (
        <div className="mb-6 p-5 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("ws-templates-recommended-title", "Plantillas recomendadas por Fluxi")}
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  {t(
                    "ws-templates-recommended-desc",
                    "Crea en un click las plantillas que el flujo de pedidos necesita. Quedan en PENDING hasta que Meta las apruebe (unos minutos)."
                  )}
                </p>
                <p className="text-[11px] text-gray-500 mt-2">
                  {t("ws-templates-recommended-missing", "Faltan")}:{" "}
                  {recommendedMissing
                    .map((k) => PRESETS[k].suggestedName)
                    .join(", ")}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowRecommendedModal(true)}
              disabled={creatingRecommended}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
            >
              <Sparkles className="w-4 h-4" />
              {creatingRecommended
                ? t("ws-templates-recommended-creating", "Creando...")
                : t("ws-templates-recommended-cta", "Crear recomendadas")}
            </button>
          </div>
        </div>
      )}


      {/* Defaults */}
      <section className="mb-8 p-5 bg-white border border-gray-200 rounded-2xl">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          {t("ws-templates-defaults-title", "Plantillas por defecto")}
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          {t(
            "ws-templates-defaults-desc",
            "Selecciona qué plantilla aprobada se usa en cada caso. Solo puedes asignar plantillas con estado APPROVED."
          )}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DefaultSelector
            label={t("ws-templates-default-confirmation", "Confirmación de pedido")}
            value={account.confirmation_template_name ?? ""}
            templates={approvedTemplates}
            notice={getDefaultNotice("confirmation_template_name")}
            saving={savingDefault === "confirmation_template_name"}
            onChange={(value) => handleSetDefault("confirmation_template_name", value)}
          />
          <DefaultSelector
            label={t("ws-templates-default-order-confirmed", "Pedido confirmado (opcional)")}
            value={account.order_confirmed_template_name ?? ""}
            templates={approvedTemplates}
            notice={getDefaultNotice("order_confirmed_template_name")}
            saving={savingDefault === "order_confirmed_template_name"}
            required={false}
            emptyLabel={t("ws-templates-use-ai-response", "Sin plantilla, usar respuesta de IA")}
            helperText={t(
              "ws-templates-order-confirmed-optional-hint",
              "Opcional. Si queda vacío, después de confirmar responde la IA como de costumbre."
            )}
            onChange={(value) => handleSetDefault("order_confirmed_template_name", value)}
          />
          <DefaultSelector
            label={t("ws-templates-default-reminder", "Recordatorio")}
            value={account.reminder_template_name ?? ""}
            templates={approvedTemplates}
            notice={getDefaultNotice("reminder_template_name")}
            saving={savingDefault === "reminder_template_name"}
            onChange={(value) => handleSetDefault("reminder_template_name", value)}
          />
          <DefaultSelector
            label={t("ws-templates-default-fallback", "Fallback (dirección incompleta)")}
            value={account.fallback_template_name ?? ""}
            templates={approvedTemplates}
            notice={getDefaultNotice("fallback_template_name")}
            saving={savingDefault === "fallback_template_name"}
            onChange={(value) => handleSetDefault("fallback_template_name", value)}
          />
          <DefaultSelector
            label={t("ws-templates-default-cart-recovery", "Recuperación de carrito (carritos abandonados)")}
            value={account.cart_recovery_template_name ?? ""}
            templates={approvedTemplates}
            notice={getDefaultNotice("cart_recovery_template_name")}
            saving={savingDefault === "cart_recovery_template_name"}
            onChange={(value) => handleSetDefault("cart_recovery_template_name", value)}
          />
        </div>
      </section>

      {/* Template list */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          {t("ws-templates-list-title", "Todas las plantillas")}
        </h3>
        {templates.length === 0 ? (
          <div className="p-10 text-center border border-dashed border-gray-300 rounded-2xl">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {t(
                "ws-templates-empty",
                "Aún no tienes plantillas. Sincroniza con Meta o crea una nueva."
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((tpl) => {
              const style = STATUS_STYLE[tpl.status] ?? STATUS_STYLE.DISABLED;
              return (
                <div
                  key={tpl.id}
                  className="p-4 bg-white border border-gray-200 rounded-2xl flex flex-col md:flex-row md:items-start md:justify-between gap-4"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-medium text-sm text-gray-900">{tpl.name}</span>
                      <span className="text-xs text-gray-500 uppercase">{tpl.language}</span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border ${style.bg} ${style.text}`}
                      >
                        <style.Icon className="w-3 h-3" />
                        {tpl.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {tpl.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        {tpl.variable_count} {t("ws-templates-variables", "variables")}
                      </span>
                    </div>
                    {tpl.body_text && (
                      <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-3">{tpl.body_text}</p>
                    )}
                    {tpl.status === "REJECTED" && (
                      <div className="mt-2 p-2.5 bg-red-50 border border-red-100 rounded-lg">
                        {tpl.rejection_reason && tpl.rejection_reason !== "NONE" ? (
                          <p className="text-xs text-red-700 font-medium">
                            {t("ws-templates-rejection-reason", "Razón de rechazo")}: {tpl.rejection_reason}
                          </p>
                        ) : (
                          <p className="text-xs text-red-700 font-medium">
                            {t("ws-templates-rejection-no-reason", "Meta rechazó sin razón específica")}
                          </p>
                        )}
                        <p className="text-[11px] text-red-600 mt-1">
                          {t(
                            "ws-templates-rejection-hint",
                            "Suele ser un rate-limit de templates por día o un quality rating bajo del WABA. Revisa business.facebook.com/wa/manage/message-templates. Si sigue fallando, intenta crear el template directamente desde Meta Business Manager y después sincroniza aquí."
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1 flex-wrap self-end md:self-auto flex-shrink-0">
                    <button
                      onClick={() => setViewTarget(tpl)}
                      className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors inline-flex items-center gap-1.5"
                      aria-label={t("ws-templates-view-full", "Ver completa")}
                      title={t("ws-templates-view-full", "Ver completa")}
                    >
                      <Eye className="w-4 h-4" />
                      {t("ws-templates-view-full", "Ver completa")}
                    </button>
                    <button
                      onClick={() => openEditModal(tpl)}
                      className="px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors inline-flex items-center gap-1.5"
                      aria-label={t("ws-templates-edit", "Editar plantilla")}
                      title={t("ws-templates-edit", "Editar plantilla")}
                    >
                      <Pencil className="w-4 h-4" />
                      {t("ws-templates-edit-short", "Editar")}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(tpl)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label={t("ws-templates-delete", "Eliminar")}
                      title={t("ws-templates-delete", "Eliminar")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {showCreateModal && (
        <CreateTemplateModal
          ownerId={ownerId!}
          initialTemplate={editTarget}
          onClose={closeCreateModal}
          onCreated={(tpl) => {
            if (editTarget && tpl.status !== "APPROVED") {
              const slotsToClear = DEFAULT_SLOTS.filter(
                (slot) => account?.[slot] === editTarget.name
              );
              if (slotsToClear.length > 0) {
                setAccount((prev) => {
                  if (!prev) return prev;
                  const next = { ...prev };
                  slotsToClear.forEach((slot) => {
                    next[slot] = null;
                  });
                  return next;
                });
                setClearedDefaultSlots((prev) => {
                  const next = { ...prev };
                  slotsToClear.forEach((slot) => {
                    next[slot] = true;
                  });
                  return next;
                });
              }
            }
            setTemplates((prev) => {
              const exists = prev.some((item) => item.id === tpl.id);
              return exists
                ? prev.map((item) => (item.id === tpl.id ? tpl : item))
                : [tpl, ...prev];
            });
            closeCreateModal();
            toast.success(
              editTarget
                ? t("ws-templates-updated", "Plantilla actualizada en Meta")
                : t("ws-templates-created", "Plantilla enviada a Meta (PENDING)")
            );
          }}
        />
      )}

      {viewTarget && (
        <ViewTemplateModal
          template={viewTarget}
          onClose={() => setViewTarget(null)}
          onEdit={() => {
            openEditModal(viewTarget);
            setViewTarget(null);
          }}
        />
      )}

      {showRecommendedModal && (
        <RecommendedTemplatesModal
          missing={recommendedMissing}
          creating={creatingRecommended}
          onClose={() => !creatingRecommended && setShowRecommendedModal(false)}
          onCreate={async (selected) => {
            await createRecommendedTemplates(selected);
            setShowRecommendedModal(false);
          }}
        />
      )}

      <BaseModal
        isOpen={!!deleteTarget}
        onClose={() => !deletingInFlight && setDeleteTarget(null)}
        imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
        imageAlt="Warning"
        title={t("ws-templates-delete-title", "Eliminar plantilla")}
        description={
          deleteTarget ? (
            <span className="font-bold text-gray-900">
              {t(
                "ws-templates-delete-modal-desc",
                `Vas a eliminar "${deleteTarget.name}". Meta bloquea reutilizar ese mismo nombre durante 4 semanas — después de ese tiempo podrás volver a crear otra plantilla con el nombre "${deleteTarget.name}". Esta acción también la borra en Meta y no se puede deshacer.`
              )
                .split("{name}")
                .join(deleteTarget.name)}
            </span>
          ) : (
            ""
          )
        }
        buttonOne={{
          text: dictionary["cancel"]?.[language] ?? "Cancelar",
          onClick: () => setDeleteTarget(null),
          disabled: deletingInFlight,
        }}
        buttonTwo={{
          text: dictionary["delete"]?.[language] ?? "Eliminar",
          onClick: handleDeleteConfirmed,
          loading: deletingInFlight,
          disabled: deletingInFlight,
        }}
      />
    </div>
  );
};

const DefaultSelector = ({
  label,
  value,
  templates,
  notice,
  saving,
  required = true,
  emptyLabel: emptyLabelOverride,
  helperText,
  onChange,
}: {
  label: string;
  value: string;
  templates: WhatsAppTemplate[];
  notice?: string;
  saving: boolean;
  required?: boolean;
  emptyLabel?: string;
  helperText?: string;
  onChange: (value: string) => void;
}) => {
  const { language } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isUnset = !value;
  const selectedTemplate = templates.find((tpl) => tpl.name === value);
  const isInvalidDefault = Boolean(value) && !selectedTemplate;
  const hasOptions = templates.length > 0;
  const canOpen = !saving && (hasOptions || (!required && Boolean(value)));
  const pendingNotice =
    notice ||
    dictionary["ws-templates-default-cleared-after-update"]?.[language] ||
    "Esta plantilla quedó vacía porque la actualizaste. Espera a que Meta la apruebe y vuelve a seleccionarla.";
  const emptyLabel = emptyLabelOverride || dictionary["ws-templates-choose-template"]?.[language] || "Selecciona una plantilla";

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSelect = (templateName: string) => {
    onChange(templateName);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}
        {required ? (
          <span className="text-red-500"> *</span>
        ) : (
          <span className="ml-1 text-[10px] font-medium text-gray-400">
            {dictionary["ws-templates-optional"]?.[language] ?? "Opcional"}
          </span>
        )}
      </label>
      <button
        type="button"
        onClick={() => {
          if (canOpen) setOpen((prev) => !prev);
        }}
        disabled={!canOpen}
        className={`w-full min-h-[46px] px-3.5 py-2.5 border rounded-xl bg-white text-left transition-all flex items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-60 disabled:cursor-not-allowed ${
          isInvalidDefault
            ? "border-amber-300 bg-amber-50/40"
            : isUnset
              ? "border-gray-200 text-gray-500 hover:border-gray-300"
              : "border-gray-200 text-gray-900 hover:border-gray-300"
        }`}
      >
        <span className="min-w-0 flex items-center gap-2">
          {selectedTemplate ? (
            <>
              <span className="truncate text-sm font-medium">{selectedTemplate.name}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 rounded-md px-1.5 py-0.5">
                {selectedTemplate.language}
              </span>
            </>
          ) : (
            <span className="truncate text-sm">
              {isInvalidDefault ? pendingNotice : hasOptions ? emptyLabel : "No hay plantillas aprobadas"}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl shadow-gray-900/10">
          <div className="max-h-64 overflow-y-auto p-1">
            {templates.map((tpl) => {
              const selected = tpl.name === value;
              return (
                <button
                  key={`${tpl.name}-${tpl.language}`}
                  type="button"
                  onClick={() => handleSelect(tpl.name)}
                  className={`w-full px-3 py-2.5 rounded-lg text-left flex items-center justify-between gap-3 transition-colors ${
                    selected ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{tpl.name}</span>
                    <span className={`text-[10px] font-semibold uppercase ${selected ? "text-white/70" : "text-gray-400"}`}>
                      {tpl.language}
                    </span>
                  </span>
                  {selected && <Check className="w-4 h-4 flex-shrink-0" />}
                </button>
              );
            })}
            {!required && value && (
              <button
                type="button"
                onClick={() => handleSelect("")}
                className="w-full px-3 py-2.5 rounded-lg text-left text-sm text-gray-500 hover:bg-gray-50 border-t border-gray-100"
              >
                {emptyLabel}
              </button>
            )}
          </div>
        </div>
      )}

      {isInvalidDefault && (
        <p className="text-[11px] text-amber-700 mt-1.5">
          {pendingNotice}
        </p>
      )}
      {isUnset && required && (
        <p className="text-[11px] text-gray-500 mt-1.5">
          {hasOptions
            ? dictionary["ws-templates-required"]?.[language] ?? "Debes asignar una plantilla. Usa 'Crear recomendadas' si aún no tienes."
            : "Sin plantillas aprobadas disponibles."}
        </p>
      )}
      {!required && helperText && (
        <p className="text-[11px] text-gray-500 mt-1.5">
          {helperText}
        </p>
      )}
    </div>
  );
};

const RecommendedTemplatesModal = ({
  missing,
  creating,
  onClose,
  onCreate,
}: {
  missing: Array<Exclude<Preset, "custom">>;
  creating: boolean;
  onClose: () => void;
  onCreate: (selected: Array<Exclude<Preset, "custom">>) => Promise<void>;
}) => {
  const { language } = useAuth();
  const t = (key: string, fallback: string) => dictionary[key]?.[language] ?? fallback;
  const [selected, setSelected] = useState<Array<Exclude<Preset, "custom">>>(missing);

  const toggle = (key: Exclude<Preset, "custom">) => {
    setSelected((prev) =>
      prev.includes(key)
        ? prev.filter((item) => item !== key)
        : [...prev, key]
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {t("ws-templates-recommended-select-title", "Crear plantillas recomendadas")}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {t(
                "ws-templates-recommended-select-desc",
                "Selecciona cuáles quieres enviar a Meta para aprobación."
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={creating}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg disabled:opacity-50"
            aria-label={t("ws-templates-close", "Cerrar")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {missing.map((key) => {
            const spec = PRESETS[key];
            const checked = selected.includes(key);
            return (
              <label
                key={key}
                className={`block p-4 border rounded-xl cursor-pointer transition-colors ${
                  checked
                    ? "border-indigo-300 bg-indigo-50"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(key)}
                    disabled={creating}
                    className="w-4 h-4 mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {spec.suggestedName}
                      </span>
                      <span className="text-xs text-gray-500">{spec.category}</span>
                      <span className="text-xs text-gray-500">
                        {spec.variables} {t("ws-templates-variables", "variables")}
                      </span>
                      {spec.buttons && (
                        <span className="text-xs text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-md">
                          {t("ws-templates-with-buttons-short", "con botones")}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-2 mt-2">
                      {spec.recommendedBody}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}

          <div className="flex justify-between items-center gap-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              {selected.length} {t("ws-templates-selected", "seleccionadas")}
            </span>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={creating}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg disabled:opacity-50"
              >
                {t("ws-templates-cancel", "Cancelar")}
              </button>
              <button
                onClick={() => onCreate(selected)}
                disabled={creating || selected.length === 0}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {creating
                  ? t("ws-templates-recommended-creating", "Creando...")
                  : t("ws-templates-recommended-create-selected", "Crear seleccionadas")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewTemplateModal = ({
  template,
  onClose,
  onEdit,
}: {
  template: WhatsAppTemplate;
  onClose: () => void;
  onEdit: () => void;
}) => {
  const { language } = useAuth();
  const t = (key: string, fallback: string) => dictionary[key]?.[language] ?? fallback;
  const body = getTemplateBody(template);
  const buttons = getTemplateButtons(template);
  const previewBody = renderTemplatePreviewBody(body, template.variable_count);
  const previewVariables = getPreviewVariables(body, template.variable_count);
  const hasImageHeader = getTemplateHeaderFormat(template) === "IMAGE";
  const headerImageUrl = getTemplateHeaderImageUrl(template);
  const style = STATUS_STYLE[template.status] ?? STATUS_STYLE.DISABLED;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900 truncate">
              {template.name}
            </h3>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className="text-xs text-gray-500 uppercase">{template.language}</span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border ${style.bg} ${style.text}`}
              >
                <style.Icon className="w-3 h-3" />
                {template.status}
              </span>
              <span className="text-xs text-gray-500">{template.category}</span>
              <span className="text-xs text-gray-500">
                {template.variable_count} {t("ws-templates-variables", "variables")}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            aria-label={t("ws-templates-close", "Cerrar")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">
              {t("ws-templates-preview", "Vista previa")}
            </p>
            <div
              className="rounded-2xl border border-gray-200 p-5"
              style={{
                backgroundColor: "#efe7dc",
                backgroundImage:
                  "radial-gradient(circle at 18px 18px, rgba(0,0,0,0.035) 1.5px, transparent 1.5px)",
                backgroundSize: "32px 32px",
              }}
            >
              <div className="max-w-md">
                <div className="relative bg-white rounded-xl rounded-tl-sm shadow-sm border border-black/5">
                  <span className="absolute -left-2 top-0 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent" />
                  {hasImageHeader && (
                    <div className="mx-3 mt-3 h-40 overflow-hidden rounded-lg border border-gray-100 bg-gray-100">
                      {headerImageUrl ? (
                        <img
                          src={headerImageUrl}
                          alt={t("ws-templates-image-header-alt", "Imagen de la plantilla")}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-50 text-gray-500">
                          <ImageIcon className="h-8 w-8" />
                          <span className="text-xs font-medium">
                            {t("ws-templates-product-image-preview", "Imagen del producto")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="px-4 py-3">
                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-900">
                      {previewBody || t("ws-templates-no-body", "Sin cuerpo registrado")}
                    </p>
                    <div className="text-right text-[11px] text-gray-400 mt-1">
                      {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  {buttons.length > 0 && (
                    <div className="border-t border-gray-200">
                      {buttons.map((button) => (
                        <button
                          key={button}
                          type="button"
                          className="w-full px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50 border-b border-gray-200 last:border-b-0"
                        >
                          {button}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {previewVariables.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">
                {t("ws-templates-preview-vars", "Variables de ejemplo")}
              </p>
              <div className="flex flex-wrap gap-2">
                {previewVariables.map((variable) => (
                  <span
                    key={variable.token}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg"
                  >
                    <span className="font-mono text-gray-400">{variable.token}</span>{" "}
                    {variable.value}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">
              {t("ws-templates-field-body", "Cuerpo del mensaje")}
            </p>
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
              <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">
                {body || t("ws-templates-no-body", "Sin cuerpo registrado")}
              </p>
            </div>
          </div>

          {template.rejection_reason && template.rejection_reason !== "NONE" && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-xs font-semibold text-red-700">
                {t("ws-templates-rejection-reason", "Razón de rechazo")}
              </p>
              <p className="text-sm text-red-700 mt-1">{template.rejection_reason}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-500">
            <div>
              <span className="font-medium text-gray-600">Meta ID: </span>
              {template.meta_template_id || "-"}
            </div>
            <div>
              <span className="font-medium text-gray-600">
                {t("ws-templates-last-sync", "Última sync")}:{" "}
              </span>
              {template.last_synced_at
                ? new Date(template.last_synced_at).toLocaleString()
                : "-"}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg"
            >
              {t("ws-templates-close", "Cerrar")}
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center gap-2"
            >
              <Pencil className="w-4 h-4" />
              {t("ws-templates-edit", "Editar plantilla")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==============================================================
// Create Template Modal
// ==============================================================

type Preset =
  | "confirmation"
  | "reminder"
  | "fallback"
  | "cart_recovery"
  | "order_confirmed"
  | "dropi_guia_generada"
  | "dropi_en_camino"
  | "dropi_entregado"
  | "dropi_devuelto"
  | "dropi_cancelado"
  | "custom";

interface PresetSpec {
  variables: number;
  buttons: boolean;
  headerFormat?: "IMAGE";
  /** Canonical name we suggest when creating the recommended template. */
  suggestedName: string;
  /** Meta category that matches the intent. */
  category: TemplateCategory;
  /** Human label for each {{n}} placeholder, in order. length MUST === variables. */
  variableLabels: string[];
  /** Body used by the "Crear recomendadas" bulk action. The modal opens with
   *  an empty body so the owner builds the message themselves via the
   *  variable buttons. */
  recommendedBody: string;
}

const PRESETS: Record<Exclude<Preset, "custom">, PresetSpec> = {
  confirmation: {
    variables: 4,
    buttons: true,
    headerFormat: "IMAGE",
    suggestedName: "confirmacion_con_imagen_v2",
    category: "UTILITY",
    variableLabels: [
      "Nombre del cliente",
      "Productos del pedido",
      "Total",
      "Dirección completa",
    ],
    recommendedBody:
      "Hola, {{1}}\n\n" +
      "Queremos confirmar los detalles de tu pedido y la dirección en donde será entregado:\n" +
      "✔ Producto: {{2}}\n" +
      "✔ Valor: ${{3}}\n" +
      "📍 Dirección: {{4}}\n\n" +
      "Si toda la información es correcta, pulsa en Confirmar Pedido para continuar con el envío\n" +
      "👇",
  },
  reminder: {
    variables: 4,
    buttons: false,
    suggestedName: "order_recordatorio_fluxi",
    category: "UTILITY",
    variableLabels: [
      "Nombre del cliente",
      "Número de pedido",
      "Productos",
      "Total",
    ],
    recommendedBody:
      "Hola {{1}}, te recordamos tu pedido #{{2}}:\n\n" +
      "📦 Productos: {{3}}\n" +
      "💰 Total: ${{4}}\n\n" +
      "¿Podrías confirmar que los datos son correctos?",
  },
  fallback: {
    variables: 5,
    buttons: false,
    suggestedName: "order_direccion_fluxi",
    category: "UTILITY",
    variableLabels: [
      "Nombre del cliente",
      "Número de pedido",
      "Producto",
      "Cantidad (unidades)",
      "Total",
    ],
    recommendedBody:
      "Hola {{1}}, recibimos tu pedido #{{2}}:\n\n" +
      "📦 Producto: {{3}}\n" +
      "📦 Unidades: {{4}}\n" +
      "💰 Total: ${{5}}\n\n" +
      "Nos falta tu dirección de envío. ¿Podrías compartirla? Envíanos: dirección, ciudad y departamento.",
  },
  cart_recovery: {
    variables: 4,
    buttons: false,
    suggestedName: "cart_recovery_fluxi",
    category: "MARKETING",
    variableLabels: [
      "Nombre del cliente",
      "Producto",
      "Cantidad (unidades)",
      "Total",
    ],
    recommendedBody:
      "Hola {{1}}, vimos que dejaste tu compra incompleta 🛒\n\n" +
      "📦 Producto: {{2}}\n" +
      "📦 Unidades: {{3}}\n" +
      "💰 Total: ${{4}}\n\n" +
      "¿Quieres que te ayudemos a finalizarla? Solo respóndenos por aquí.",
  },
  order_confirmed: {
    variables: 0,
    buttons: false,
    suggestedName: "pedido_confirmado_default",
    category: "UTILITY",
    variableLabels: [],
    recommendedBody:
      "🎉 ¡Tu pedido ya está confirmado!\n" +
      "Estamos seguros de que lo vas a disfrutar muchísimo.\n" +
      "📦 Llegará a tu puerta en un lapso de 3 a 5 días hábiles.\n\n" +
      "¡Gracias por confiar en nosotros! 💚",
  },
  dropi_guia_generada: {
    variables: 3,
    buttons: false,
    suggestedName: "dropi_guia_generada",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Número de guía"],
    recommendedBody:
      "Hola {{1}}, tu pedido #{{2}} ya fue procesado y está listo para ser enviado.\n\n" +
      "📦 Guía de envío: {{3}}\n\n" +
      "Te avisaremos cuando esté en camino.",
  },
  dropi_en_camino: {
    variables: 3,
    buttons: false,
    suggestedName: "dropi_en_camino",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Número de guía"],
    recommendedBody:
      "Hola {{1}}, tu pedido #{{2}} ya está en camino 🚚\n\n" +
      "📦 Guía: {{3}}\n\n" +
      "Por favor ten el dinero listo para pagar contra entrega.",
  },
  dropi_entregado: {
    variables: 3,
    buttons: false,
    suggestedName: "dropi_entregado",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Mensaje"],
    recommendedBody:
      "¡Listo {{1}}! Tu pedido #{{2}} fue entregado. 🎉\n\n" +
      "{{3}}\n\n" +
      "¡Esperamos que lo disfrutes!",
  },
  dropi_devuelto: {
    variables: 3,
    buttons: false,
    suggestedName: "dropi_devuelto",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Motivo"],
    recommendedBody:
      "Hola {{1}}, hubo un problema con la entrega de tu pedido #{{2}}.\n\n" +
      "{{3}}\n\n" +
      "Te contactaremos pronto para resolverlo.",
  },
  dropi_cancelado: {
    variables: 3,
    buttons: false,
    suggestedName: "dropi_cancelado",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Motivo"],
    recommendedBody:
      "Hola {{1}}, tu pedido #{{2}} ha sido cancelado.\n\n" +
      "{{3}}\n\n" +
      "Si necesitas ayuda, escríbenos.",
  },
};

/** Counts the highest {{n}} placeholder in body text. */
const countBodyVariables = (text: string): number => {
  let max = 0;
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = parseInt(m[1], 10);
    if (!isNaN(n)) max = Math.max(max, n);
  }
  return max;
};

const CreateTemplateModal = ({
  ownerId,
  initialTemplate,
  onClose,
  onCreated,
}: {
  ownerId: string;
  initialTemplate?: WhatsAppTemplate | null;
  onClose: () => void;
  onCreated: (tpl: WhatsAppTemplate) => void;
}) => {
  const { language } = useAuth();
  const t = (key: string, fallback: string) => dictionary[key]?.[language] ?? fallback;

  const isEditing = Boolean(initialTemplate);
  const [preset, setPreset] = useState<Preset>(isEditing ? "custom" : "confirmation");
  const [name, setName] = useState(
    initialTemplate ? initialTemplate.name : PRESETS.confirmation.suggestedName
  );
  const [lang, setLang] = useState<string>(initialTemplate?.language || "es");
  const [category, setCategory] = useState<TemplateCategory>(
    initialTemplate?.category || PRESETS.confirmation.category
  );
  const [body, setBody] = useState(initialTemplate ? getTemplateBody(initialTemplate) : "");
  const [withButtons, setWithButtons] = useState(
    initialTemplate ? getTemplateButtons(initialTemplate).length > 0 : PRESETS.confirmation.buttons
  );
  const [submitting, setSubmitting] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const handlePreset = (p: Preset) => {
    setPreset(p);
    setBody("");
    if (p !== "custom") {
      const spec = PRESETS[p];
      setWithButtons(spec.buttons);
      setName(spec.suggestedName);
      setCategory(spec.category);
    } else {
      setName("");
      setWithButtons(false);
    }
  };

  const activeSpec = preset !== "custom" ? PRESETS[preset] : null;
  const currentVariableCount = countBodyVariables(body);

  /** Insert a placeholder at the current caret position, or append it if the
   *  textarea isn't focused. Keeps focus + positions the caret right after
   *  the inserted token so the owner can keep typing naturally. */
  const insertAtCursor = (token: string) => {
    const ta = bodyRef.current;
    if (!ta) {
      setBody((prev) => prev + token);
      return;
    }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    const caret = start + token.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  };

  const handleSubmit = async () => {
    const cleanName = name.trim().toLowerCase();
    if (!cleanName || !/^[a-z0-9_]+$/.test(cleanName)) {
      toast.error(t("ws-templates-name-error", "El nombre debe ser alfanumérico con underscores"));
      return;
    }
    const cleanBody = body.trim();
    if (!cleanBody) {
      toast.error(t("ws-templates-body-error", "El cuerpo no puede estar vacío"));
      return;
    }
    // Meta rejects templates whose body starts or ends with a variable
    // (`Leading or Trailing Params Not Allowed`, error_subcode 2388299).
    // Validate locally so the user sees a clear message before we hit Meta.
    if (/^\s*\{\{\d+\}\}/.test(cleanBody)) {
      toast.error(t(
        "ws-templates-variable-at-start-error",
        "El cuerpo no puede empezar con una variable ({{1}}, {{2}}…). Agrega texto antes."
      ));
      return;
    }
    if (/\{\{\d+\}\}\s*$/.test(cleanBody)) {
      toast.error(t(
        "ws-templates-variable-at-end-error",
        "El cuerpo no puede terminar con una variable ({{1}}, {{2}}…). Agrega texto después."
      ));
      return;
    }

    const shouldIncludeImageHeader =
      activeSpec?.headerFormat === "IMAGE" ||
      (isEditing && initialTemplate?.name === PRESETS.confirmation.suggestedName) ||
      Boolean(initialTemplate && getTemplateHeaderFormat(initialTemplate) === "IMAGE");
    const components = buildTemplateComponents(
      body,
      withButtons,
      shouldIncludeImageHeader ? "IMAGE" : undefined
    );

    setSubmitting(true);
    const { res, error } = await handleApi(
      isEditing && initialTemplate
        ? updateTemplate(initialTemplate.id, {
            owner_id: ownerId,
            category,
            components,
          })
        : createTemplate({
            owner_id: ownerId,
            name: cleanName,
            language: lang,
            category,
            components,
          }),
      false
    );
    if (res) {
      onCreated(res);
    } else if (error) {
      const info = extractMetaError(error);
      toast.error(info.message.slice(0, 250));
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">
            {isEditing
              ? t("ws-templates-edit-modal-title", "Editar plantilla")
              : t("ws-templates-modal-title", "Crear plantilla")}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            aria-label={t("ws-templates-close", "Cerrar")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Preset */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">
              {t("ws-templates-preset", "Usar como")}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {(["confirmation", "order_confirmed", "reminder", "fallback", "cart_recovery", "custom"] as Preset[]).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePreset(p)}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                    preset === p
                      ? "border-gray-900 bg-gray-50 text-gray-900"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {t(`ws-templates-preset-${p}`, p)}
                </button>
              ))}
            </div>
            {preset !== "custom" && (
              <p className="text-xs text-gray-500 mt-2">
                {t("ws-templates-preset-hint", "Este preset crea una plantilla con la estructura que el flujo de pedidos espera. Si modificas el cuerpo, mantén el mismo número de variables {{1}}..{{n}}.")}
              </p>
            )}
            {isEditing && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-2">
                {t(
                  "ws-templates-edit-hint",
                  "Se actualizará esta plantilla en Meta sin cambiar su nombre. Meta puede volver a revisarla antes de dejarla disponible."
                )}
              </p>
            )}
          </div>

          {/* Name + Language + Category */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t("ws-templates-field-name", "Nombre")}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isEditing}
                placeholder="order_confirmation_v2"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t("ws-templates-field-language", "Idioma")}
              </label>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                disabled={isEditing}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
              >
                {LANGUAGE_OPTIONS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t("ws-templates-field-category", "Categoría")}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                disabled={isEditing && initialTemplate?.status === "APPROVED"}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Body + variable chips */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t("ws-templates-field-body", "Cuerpo del mensaje")}
            </label>

            {/* Variable chips — clickable tokens that insert {{n}} at the
                current cursor position. Only shown for presets where each
                {{n}} has a semantic meaning. */}
            {activeSpec && (
              <div className="mb-2">
                <p className="text-[11px] text-gray-500 mb-1.5">
                  {t(
                    "ws-templates-variables-picker-hint",
                    "Haz click en una variable para insertarla en el cuerpo:"
                  )}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {activeSpec.variableLabels.map((label, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => insertAtCursor(`{{${idx + 1}}}`)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-xs text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      <span className="font-medium">{label}</span>
                      <code className="font-mono text-[10px] opacity-70">{`{{${idx + 1}}}`}</code>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder={t(
                "ws-templates-body-placeholder",
                "Escribe el mensaje. Usa los botones de arriba para insertar variables, o escribe {{1}}, {{2}}, etc. manualmente."
              )}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">
                {t("ws-templates-body-hint", "Meta validará la sintaxis al aprobar.")}
              </p>
              <span className="text-[11px] text-gray-400">
                {t("ws-templates-body-var-count", "Variables detectadas")}: {currentVariableCount}
              </span>
            </div>
          </div>

          {/* Buttons toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={withButtons}
              onChange={(e) => setWithButtons(e.target.checked)}
              className="w-4 h-4"
            />
            {t("ws-templates-with-buttons", "Incluir botones Confirmar Pedido / Modificar Datos (quick_reply)")}
          </label>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg"
            >
              {t("ws-templates-cancel", "Cancelar")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {submitting
                ? t("ws-templates-submitting", "Enviando a Meta...")
                : isEditing
                  ? t("ws-templates-edit-submit", "Guardar cambios en Meta")
                  : t("ws-templates-submit", "Crear y enviar a Meta")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPanel;
